import { ChevronDown, ChevronRight, PencilLine } from 'lucide-react';
import type { ReactNode } from 'react';
import { ICP_GROUP_HOW, ICP_GROUP_LABELS, ICP_MUST_RATING, ICP_RATINGS, ICP_RATING_LABELS, ICP_READ_NEW_DELAY_MS, type IcpItem, type IcpRating, type IcpView, type RankedBy } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { RatingDots } from '../../../../components/RatingDots';
import { Skeleton } from '../../../../components/Skeleton';
import { factWhenOf, groupsOf } from '../../icp-view';
import styles from '../../../icp/components/icp.module.css';
import noteStyles from './IcpCardNote.module.css';

// ADR 0061/0062, mockup M2: the "Ideal candidate" card above the list. It shows every line with its 1–5 dots — change a number and the
// table re-orders at once, with no new search — and, in its footer, what Jev has cost on this job and what it is doing now. A job with
// no profile has no card: Ranking points at job setup instead.

const READ_ONLY_TITLE = 'You can see this job’s ranking but can’t change it.';
const EDITING_NOTE = 'Editing in the panel.';
const DEFAULT_SORT_NOTE = 'Rated 5 means must have: people who show every 5 come first.';
const FLASH_NOTE = 'Order updated. No new Jev search, no cost.';
const RATING_SCALE = ['1 Barely matters', '3 Matters', '5 Must have'] as const;
const MICRO_USD_PER_DOLLAR = 1_000_000;
const MS_PER_SECOND = 1_000;
const NEW_CVS_NOTE = `New CVs are read on their own, about ${ICP_READ_NEW_DELAY_MS / MS_PER_SECOND} seconds after they arrive.`;

/** "$1.24": what Jev has cost on this job, worked out from its asks (never stored, #10). */
export function jevSpentLabel(microUsd: number): string {
  return `$${(microUsd / MICRO_USD_PER_DOLLAR).toFixed(2)}`;
}

type CardState = { readonly kind: 'loading' } | { readonly kind: 'error'; readonly onRetry: () => void } | { readonly kind: 'built' };

interface IcpCardProps {
  readonly state: CardState;
  /** The lines with the ratings on screen (the server's, with unconfirmed clicks laid over). */
  readonly items: readonly IcpItem[];
  /** Lines no reading covers yet (new or reworded): left out of the order until read. */
  readonly staleIds: ReadonlySet<string>;
  /** Nothing differs from the ratings the form last saved: Reset focus has nothing to do. */
  readonly isAtBaseline: boolean;
  readonly canEdit: boolean;
  /** The form is open in the panel: the dots wait for it. */
  readonly isEditing: boolean;
  /** The full ask is reading CVs now. */
  readonly reading: { readonly done: number; readonly total: number } | null;
  /** Why the newest ask stopped, with "Rank again". */
  readonly askProblem: string | null;
  /** ADR 0062: the engine behind the order on screen; 'criteria' while a job is still being moved. */
  readonly rankedBy: RankedBy;
  /** The profile has lines Jev reads, so no key means those lines are left out. */
  readonly readsCvs: boolean;
  readonly isJevConnected: boolean;
  readonly readingNew: IcpView['readingNew'];
  /** Mockup M2: "Last read: 40 new CVs · 3.1¢", shown in the footer when nothing is being read. */
  readonly lastRead: IcpView['lastRead'];
  readonly spentMicroUsd: number;
  readonly autoPausedReason: string | null;
  /** A closed job in its old order is not read unless someone asks. */
  readonly isJobClosed: boolean;
  /** What the move from the screening criteria left out or capped; shown until someone has seen them. */
  readonly migrationNotes: readonly string[];
  readonly onNotesSeen: () => void;
  readonly flash: boolean;
  readonly isRanking: boolean;
  /** The lines are folded to the one-line summary; the focus bar above the table carries the numbers meanwhile. */
  readonly isFolded: boolean;
  readonly onFoldedChange: (isFolded: boolean) => void;
  readonly onRate: (itemId: string, rating: IcpRating) => void;
  readonly onReset: () => void;
  readonly onEdit: () => void;
  readonly onRank: () => void;
  /** ADR 0070 stage 2: "Rank again" with the job's Jev budget: the cost and what is left on the button, and why it is off when it would not fit. */
  readonly rankBudget?: { readonly suffix: string; readonly blockedNote: string | null };
}

export function IcpCard(props: IcpCardProps): ReactNode {
  const { state } = props;
  if (state.kind === 'loading') {
    return (
      <div className={styles.card} aria-busy="true" aria-label="Ideal candidate">
        <Skeleton shape="card" />
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <section className={styles.card} aria-label="Ideal candidate">
        <p className={styles.problem} role="alert">
          The ideal candidate couldn’t be loaded.{' '}
          <Button size="sm" variant="outline" onClick={state.onRetry}>
            Try again
          </Button>
        </p>
      </section>
    );
  }
  return <BuiltCard {...props} />;
}

function statusOf({ reading, askProblem, rankedBy, readsCvs, isJevConnected }: Pick<IcpCardProps, 'reading' | 'askProblem' | 'rankedBy' | 'readsCvs' | 'isJevConnected'>): { readonly text: string; readonly tone?: 'warning' } {
  if (reading) return { text: `Jev is reading ${reading.done.toLocaleString('en-US')} of ${reading.total.toLocaleString('en-US')}` };
  if (askProblem) return { text: 'Ranking stopped', tone: 'warning' };
  if (rankedBy === 'criteria') return { text: 'Not ranking yet', tone: 'warning' };
  if (readsCvs && !isJevConnected) return { text: 'Ranked on facts', tone: 'warning' };
  return { text: 'Ranking with this' };
}

/** The card's footer: what Jev is doing on this job and what it has cost (mockup M2's five variants), then the order's note. */
export function cardFootOf(
  props: Pick<IcpCardProps, 'rankedBy' | 'readsCvs' | 'isJevConnected' | 'readingNew' | 'lastRead' | 'reading' | 'spentMicroUsd' | 'autoPausedReason' | 'isJobClosed' | 'isEditing' | 'flash'>,
): { readonly left: string; readonly right: string } {
  const spent = `Jev on this job so far: ${jevSpentLabel(props.spentMicroUsd)}`;
  if (props.rankedBy === 'criteria') {
    return props.reading
      ? { left: `${spent} · moving to the ideal candidate`, right: 'The profile is ready; the order switches when the reading finishes.' }
      : { left: spent, right: props.isJobClosed ? 'Nothing is read on a closed job unless you ask.' : 'The order switches once Jev has read the CVs.' };
  }
  if (props.readsCvs && !props.isJevConnected) return { left: spent, right: 'Facts are ranked for free. Jev lines are left out until a key is added.' };
  if (props.autoPausedReason) return { left: spent, right: props.autoPausedReason };
  if (props.readingNew) {
    const { total, estimateLabel } = props.readingNew;
    return { left: `Reading ${total.toLocaleString('en-US')} new ${total === 1 ? 'CV' : 'CVs'} · ${estimateLabel} · ${spent}`, right: NEW_CVS_NOTE };
  }
  const lastRead = props.lastRead ? `Last read: ${props.lastRead.count.toLocaleString('en-US')} new ${props.lastRead.count === 1 ? 'CV' : 'CVs'} · ${props.lastRead.costLabel} · ` : '';
  return { left: `${lastRead}${spent}`, right: props.isEditing ? EDITING_NOTE : props.flash ? FLASH_NOTE : DEFAULT_SORT_NOTE };
}

function BuiltCard(props: IcpCardProps): ReactNode {
  const { items, staleIds, isAtBaseline, canEdit, isEditing, askProblem, flash, isRanking, isFolded, migrationNotes, onNotesSeen, onFoldedChange, onRate, onReset, onEdit, onRank, rankBudget } = props;
  const status = statusOf(props);
  const foot = cardFootOf(props);
  const musts = items.filter((item) => item.rating === ICP_MUST_RATING);
  const isLocked = !canEdit || isEditing;
  const lockTitle = !canEdit ? READ_ONLY_TITLE : isEditing ? EDITING_NOTE : undefined;
  const Fold = isFolded ? ChevronRight : ChevronDown;
  return (
    <section className={styles.card} aria-label="Ideal candidate">
      <div className={styles.cardTop}>
        <div className={styles.cardTitleBlock}>
          <h2 className={styles.cardTitle}>
            Ideal candidate
            <span className={styles.status} data-tone={status.tone}>
              {status.text}
            </span>
          </h2>
          {/* Owner 2026-11-01 Q9: folded, the card is its title row, so the table on ICP match is in view; the must-haves stay. */}
          <span className={styles.cardSub}>
            {isFolded
              ? musts.length === 0
                ? 'No must-haves.'
                : `Must have: ${musts.map((item) => item.text).join(' · ')}`
              : 'Change any number to change what the ranking focuses on. The order updates at once, with no new search.'}
          </span>
          {isFolded && (
            <span className={styles.cardSub}>
              <span>{foot.left}</span> · <span role="status" className={flash ? styles.flash : undefined}>{foot.right}</span>
            </span>
          )}
        </div>
        <div className={styles.cardActions}>
          <Button variant="outline" icon={PencilLine} disabled={!canEdit} title={canEdit ? undefined : READ_ONLY_TITLE} onClick={onEdit}>
            Edit profile
          </Button>
          <Button variant="ghost" disabled={!canEdit || isAtBaseline || isEditing} title={lockTitle} onClick={onReset}>
            Reset focus
          </Button>
          <Button variant="ghost" icon={Fold} aria-expanded={!isFolded} onClick={() => onFoldedChange(!isFolded)}>
            {isFolded ? 'Show items' : 'Hide items'}
          </Button>
        </div>
      </div>
      {migrationNotes.length > 0 && (
        <p className={noteStyles.note} role="note">
          <span className={noteStyles.words}>
            <strong>Moved from your screening criteria:</strong> {migrationNotes.join(' ')}
          </span>
          <span className={noteStyles.actions}>
            <Button size="sm" variant="outline" disabled={!canEdit} title={canEdit ? undefined : READ_ONLY_TITLE} onClick={onEdit}>
              Check these
            </Button>
            <Button size="sm" variant="ghost" onClick={onNotesSeen}>
              Got it
            </Button>
          </span>
        </p>
      )}
      {askProblem && (
        <p className={styles.problem} role="alert">
          {askProblem}{' '}
          <Button size="sm" variant="outline" isPending={isRanking} disabled={!canEdit} isBlocked={Boolean(rankBudget?.blockedNote)} onClick={onRank}>
            Rank again{rankBudget?.suffix ?? ''}
          </Button>
          {rankBudget?.blockedNote ? <span className={styles.none}> {rankBudget.blockedNote}</span> : null}
        </p>
      )}
      {!isFolded && (
        <div className={styles.groups}>
          {groupsOf(items).map((entry) => (
            <div key={entry.group} className={styles.group}>
              <div className={styles.groupHead}>
                <h3 className={styles.groupName}>{ICP_GROUP_LABELS[entry.group]}</h3>
                <span className={styles.how} data-group={entry.group}>
                  {ICP_GROUP_HOW[entry.group]}
                </span>
              </div>
              {entry.items.map((item) => (
                <div key={item.id} className={styles.line}>
                  <span className={styles.lineTitle}>
                    <span className={styles.lineName} title={item.text}>
                      {item.text}
                    </span>
                    {item.rating === ICP_MUST_RATING && <span className={styles.tag}>Must have</span>}
                    {staleIds.has(item.id) && (
                      <span className={styles.tag} data-kind="stale" title="Jev hasn’t read this line yet, so it is left out of the order until it has.">
                        Not read yet
                      </span>
                    )}
                  </span>
                  <span className={styles.rate} title={lockTitle}>
                    <span className={styles.rateLabel}>{ICP_RATING_LABELS[item.rating]}</span>
                    <RatingDots label={`Focus for ${item.text}`} value={item.rating} hints={RATING_HINTS} isDisabled={isLocked} onChange={(value) => {
                      const rating = ICP_RATINGS.find((entry) => entry === value);
                      if (rating) onRate(item.id, rating);
                    }} />
                  </span>
                  <span className={styles.lineEvidence} title={lineEvidenceOf(item)}>
                    {lineEvidenceOf(item)}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      {/* Folded, its words sit in the title row and the focus bar above the table carries the 1-3-5 key. */}
      {!isFolded && (
      <div className={styles.cardFoot}>
        <span>{foot.left}</span>
        <span className={styles.legend}>
          {RATING_SCALE.map((entry) => (
            <span key={entry}>{entry}</span>
          ))}
        </span>
        <span role="status" className={flash ? styles.flash : undefined}>
          {foot.right}
        </span>
      </div>
      )}
    </section>
  );
}

/** One line on the card; the full words stay in its tooltip and in the form. */
function lineEvidenceOf(item: IcpItem): string {
  return item.kind === 'fact' ? `Counts as met when ${factWhenOf(item.fact)}` : item.evidence;
}

export const RATING_HINTS: Readonly<Record<string, string>> = Object.fromEntries(ICP_RATINGS.map((rating) => [String(rating), ICP_RATING_LABELS[rating]]));
